
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUser } from "@/lib/storage";
import Navbar from "@/components/Navbar";
import { useGroupsData } from "./useGroupsData";
import GroupsHeader from "./GroupsHeader";
import GroupsList from "./GroupsList";
import EmptyGroupsState from "./EmptyGroupsState";
import { AddGroupForm } from "@/components/group";

const GroupsPage = () => {
  const navigate = useNavigate();
  const { groupId } = useParams(); // Get groupId from URL params
  
  const {
    groups,
    products,
    isLoading,
    showAddGroupForm,
    setShowAddGroupForm,
    editingGroup,
    setEditingGroup,
    handleGroupSaved,
    handleEditGroup,
    handleDeleteGroup,
    handleRecordUsage
  } = useGroupsData(navigate);

  // Open usage form for specified group when navigating directly to /groups/:groupId/usage
  const [groupForUsage, setGroupForUsage] = useState<string | null>(null);
  
  useEffect(() => {
    const user = getUser();
    if (!user) {
      navigate("/");
    }
  }, [navigate]);
  
  // Handle direct navigation to usage tracking
  useEffect(() => {
    if (groupId && groups.length > 0) {
      const group = groups.find(g => g.id === groupId);
      if (group) {
        setGroupForUsage(groupId);
      }
    }
  }, [groupId, groups]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <main className="page-container">
        <GroupsHeader 
          onAddGroup={() => {
            setEditingGroup(undefined);
            setShowAddGroupForm(true);
          }} 
        />

        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">Loading groups...</p>
          </div>
        ) : groups.length === 0 ? (
          <EmptyGroupsState onCreateGroup={() => setShowAddGroupForm(true)} />
        ) : (
          <GroupsList
            groups={groups}
            products={products}
            onEdit={handleEditGroup}
            onDelete={handleDeleteGroup}
            onRecordUsage={handleRecordUsage}
            groupForUsage={groupForUsage}
            clearGroupForUsage={() => setGroupForUsage(null)}
          />
        )}

        <AddGroupForm
          open={showAddGroupForm}
          onOpenChange={setShowAddGroupForm}
          onSave={handleGroupSaved}
          initialGroup={editingGroup}
        />
      </main>
    </div>
  );
};

export default GroupsPage;
